import { useRef } from 'react'
import { MeetingProvider, useMeeting, useParticipant, VideoPlayer } from '@videosdk.live/react-sdk'
import { LuMic, LuMicOff, LuVideo, LuVideoOff } from 'react-icons/lu'
import { ControlButton, StageNotice, StageSurface, useMediaTrack } from './streamStageChrome'

/**
 * The VideoSDK half of a live stream's stage - the twin of
 * LiveKitStreamStage, same frame and same controls, driven by VideoSDK's hooks.
 * Which one mounts is decided by `session.provider`, stamped on the stream by
 * the server.
 *
 * The important difference from LiveKit: VideoSDK has no "may not publish"
 * claim in its token, so a viewer is kept off the stage by joining in
 * **RECV_ONLY** mode - the client never opens a camera or a microphone, and
 * the room has nothing to forward from them. The server does not grant
 * `allow_mod` either, so a viewer cannot mute or remove the host. Chat runs
 * over the REST endpoints for both providers, which is where "sign in to
 * speak" is actually enforced.
 */
export default function VideosdkStreamStage({ session, isHost, onEnd, onLeft }) {
  return (
    <MeetingProvider
      config={{
        meetingId: session.meeting_id,
        name: session.user_name || (isHost ? 'Astrologer' : 'Guest'),
        micEnabled: isHost,
        webcamEnabled: isHost,
        // The token is bound to this participantId; VideoSDK rejects the join
        // (4002) if the SDK announces itself as anyone else.
        ...(session.participant_id ? { participantId: String(session.participant_id) } : {}),
        mode: isHost ? 'SEND_AND_RECV' : 'RECV_ONLY',
        debugMode: false,
      }}
      token={session.token}
      // Joining must go through the SDK rather than a child effect: child
      // effects run before the provider registers the token, and a manual
      // join() then fires with no Authorization at all.
      joinWithoutUserInteraction
    >
      {isHost ? <HostStage onEnd={onEnd} onLeft={onLeft} /> : <ViewerStage onLeft={onLeft} />}
    </MeetingProvider>
  )
}

function HostStage({ onEnd, onLeft }) {
  const { toggleMic, toggleWebcam, localParticipant, localMicOn, localWebcamOn } = useMeeting({
    onMeetingLeft: () => onLeft?.(),
  })

  return (
    <div className="flex h-full flex-col">
      <StageSurface>
        {localWebcamOn && localParticipant?.id ? (
          <VideoPlayer
            participantId={localParticipant.id}
            type="video"
            containerStyle={{ width: '100%', height: '100%' }}
            className="h-full w-full"
            classNameVideo="h-full w-full object-contain"
          />
        ) : (
          <StageNotice
            title={localParticipant ? 'Your camera is off' : 'Connecting…'}
            body={
              localParticipant
                ? 'Viewers can still hear you. Turn the camera on when you are ready.'
                : 'Setting up your broadcast.'
            }
          />
        )}
      </StageSurface>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        <ControlButton
          active={Boolean(localMicOn)}
          onClick={() => toggleMic()}
          onIcon={LuMic}
          offIcon={LuMicOff}
          label={localMicOn ? 'Mute' : 'Unmute'}
        />
        <ControlButton
          active={Boolean(localWebcamOn)}
          onClick={() => toggleWebcam()}
          onIcon={LuVideo}
          offIcon={LuVideoOff}
          label={localWebcamOn ? 'Camera off' : 'Camera on'}
        />
        <button
          type="button"
          onClick={onEnd}
          className="rounded-full bg-red-600 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[.15em] text-white transition-colors hover:bg-red-700"
        >
          End stream
        </button>
      </div>
    </div>
  )
}

/** The audience's view: the one remote participant - the astrologer. */
function ViewerStage({ onLeft }) {
  const { participants, localParticipant } = useMeeting({ onMeetingLeft: () => onLeft?.() })
  const hostId = [...participants.keys()].find((id) => id !== localParticipant?.id) ?? null

  if (!hostId) {
    return (
      <StageSurface>
        <StageNotice title="Waiting for the astrologer" body="The session is live — their video should appear in a moment." />
      </StageSurface>
    )
  }
  return <HostFeed participantId={hostId} />
}

/**
 * One participant's video plus their microphone.
 *
 * VideoSDK's <VideoPlayer> carries the video track only; the mic is a separate
 * stream that has to ride its own hidden <audio> element, or the broadcast is
 * silent. Only ever for a REMOTE participant - attaching your own mic would
 * play it into your own speakers for the mic to capture again.
 */
function HostFeed({ participantId }) {
  const audioRef = useRef(null)
  const { webcamOn, micOn, micStream, displayName } = useParticipant(participantId)
  useMediaTrack(audioRef, micStream?.track, Boolean(micOn))

  return (
    <StageSurface>
      <audio ref={audioRef} autoPlay playsInline />
      {webcamOn ? (
        <VideoPlayer
          participantId={participantId}
          type="video"
          containerStyle={{ width: '100%', height: '100%' }}
          className="h-full w-full"
          classNameVideo="h-full w-full object-contain"
        />
      ) : (
        <StageNotice
          title="Camera is off"
          body={`${displayName || 'The astrologer'} is still here — you can hear the session.`}
        />
      )}
    </StageSurface>
  )
}
